/**
 * Equipment catalog resolution — merges baseline SKUs with the platform
 * Firestore overlay AND a per-company overlay (Wave B1). Mirrors the 3-layer
 * supplier directory model in `resolveSupplierDirectory.ts`.
 *
 * Precedence (later wins on id collision):
 *   1. baselineEquipmentCatalogEntries()      — offline/seed fallback
 *   2. equipment_catalog                       — platform admin (curated)
 *   3. companies/{companyId}/equipment_catalog — company admin (overlay)
 *
 * The company overlay lets a tenant override a platform SKU's price / power
 * defaults, or add private SKUs, without mutating the shared catalog. When no
 * companyId (or no company entries) is provided, resolution is byte-for-byte
 * the previous platform-only behaviour.
 */
import type { EquipmentCatalogEntry } from '../types/supplyChain';
import {
  baselineEquipmentCatalogEntries,
  fetchEquipmentCatalog,
  fetchCompanyEquipmentCatalog,
} from '../services/supplyChain/equipmentCatalogStore';

/**
 * Pure merge — used in tests and when callers already fetched overlays.
 * `company` is optional so existing 2-arg callers (the admin editors that
 * resolve platform-only) keep compiling and behaving identically.
 */
export function mergeEquipmentCatalog(
  baseline: readonly EquipmentCatalogEntry[],
  curated: readonly EquipmentCatalogEntry[],
  company: readonly EquipmentCatalogEntry[] = [],
): EquipmentCatalogEntry[] {
  const byId = new Map<string, EquipmentCatalogEntry>();
  for (const e of baseline) byId.set(e.id, e);
  for (const e of curated) byId.set(e.id, e);
  // Company overlay applied last → company wins on id collision.
  for (const e of company) byId.set(e.id, e);
  return Array.from(byId.values());
}

/**
 * Async resolve with Firestore overlays. Falls back to baseline-only on fetch
 * failure. `companyId` is optional: omit it (or pass null) for platform-only
 * resolution — both store fetches degrade to [] so the result is unchanged.
 */
export async function resolveEquipmentCatalog(
  companyId?: string | null,
): Promise<EquipmentCatalogEntry[]> {
  const [baseline, curated, company] = await Promise.all([
    Promise.resolve(baselineEquipmentCatalogEntries()),
    fetchEquipmentCatalog(),
    fetchCompanyEquipmentCatalog(companyId),
  ]);
  return mergeEquipmentCatalog(baseline, curated, company);
}
